import { useEffect } from 'react'
import { navigate } from 'gatsby'

import { I18N } from '@/app'
import { AppStatus, store } from '@/redux'
import { useFirebaseUser } from '@/features/Auth/identity/useFirebaseUser'
import { resolveProfile } from '@/features/Auth/session/resolveProfile'
import { authStore } from '@/features/Auth/store/authStore'
import { landing } from '@/features/Auth/session/landing'
import { queryClient } from '@/services/api/queryClient'
import { QueryKeys } from '@/services/api/session/queryKeys'

/**
 * Turns a state into what the app does about it.
 *
 * This is the only file in the feature that navigates, opens a modal or shows an alert, and
 * the only one that knows a destination by name. Everything upstream of it decides; nothing
 * upstream of it performs. `social.ts` used to do both in the same 443 lines, which is why
 * the flow could not be followed without running it, and why the dependency direction
 * `docs/TEMPLATE.md` requires was broken. See EPIC-005.
 *
 * Mount once. Two mounts would navigate twice for the same transition.
 */

/**
 * Whether signing in is happening inside a modal or on its own page.
 *
 * The one thing the web flow has that mobile's does not: the same steps are reachable as
 * routes and as modals, and finishing has to end wherever it started. Read at the moment of
 * the transition rather than subscribed to, so opening or closing an unrelated modal cannot
 * re-run the effect. See EPIC-005.
 */
const AUTH_MODALS = ['login', 'signup', 'authWall'] as const

type AuthModal = (typeof AUTH_MODALS)[number]

function openAuthModal(): AuthModal | null {
  const modals = store.getState().AppStatus.modals

  return AUTH_MODALS.find(name => !!modals?.[name]) ?? null
}

export function useAuthFlow() {
  const state = authStore.use()
  const firebaseUser = useFirebaseUser()

  // The machine describes signing in, so it cannot outlive the session that signing in
  // produced. Losing the user without this left states like `registering` standing with
  // nobody signed in — a signup form that had already been told it needed no password, now
  // showing the fields again, with no way to tell why.
  useEffect(() => {
    if (firebaseUser?.uid || authStore.get().status === 'anonymous') return

    authStore.dispatch({ type: 'signedOut' })
  }, [firebaseUser?.uid])

  useEffect(() => {
    switch (state.status) {
      case 'authenticating':
      case 'exchanging':
        AppStatus.set('loading')
        return

      case 'resolving':
        resolveProfile(authStore.dispatch)
        return

      case 'registering': {
        AppStatus.set('idle')

        // The alert explains why a *social* sign-in landed on a registration form nobody
        // asked for. Someone who chose e-mail is usually already standing on that form, and
        // saying it there would interrupt a form in progress.
        if (state.provider !== 'email') {
          alert(I18N.t('Login.socialWarn'))
        }

        // Not conditional on the provider. Skipping it stranded anyone whose e-mail identity
        // exists in the pool but has no profile here — they authenticate from the login
        // screen, land back on login, and are given no route to finish registering.
        const openModal = openAuthModal()

        if (!openModal) {
          navigate('/auth/signup/')
        } else if (openModal !== 'signup') {
          // Whichever modal is standing, not `login` by name: the registration wall hosts the
          // same form under `authWall`, and transitioning from a modal that is not open
          // leaves the person looking at the one they started in.
          AppStatus.transitionModals([openModal, 'signup'])
        }

        return
      }

      case 'ready': {
        const openModal = openAuthModal()

        // `QueryKeys.me.refresh()` invalidates and returns nothing, so awaiting it does not
        // wait for the profile to arrive. Refetching does, and the landing below depends on
        // it: navigating first showed the feed to someone the app still thought was anonymous.
        queryClient.client
          .refetchQueries({ queryKey: QueryKeys.me.key() })
          .then(async () => {
            // Every branch settles the status. `useSignup` sets `loading` and never clears
            // it, on the assumption that whatever ends the flow will — so a branch that
            // only opens a modal leaves the full-screen loader on top of it forever. That
            // is what happened to signup: login worked because its branch reached `done`.
            //
            // A registration ends on its own confirmation step; a resolved profile has no
            // such step and simply lands. Both endings live here rather than half here and
            // half in the signup form, which is what produced a confirmation being replaced
            // by a redirect.
            if (state.viaRegistration) {
              // `idle`, not `done`: the confirmation modal *is* the success step, and the
              // `done` overlay before it is a second full-screen state saying the same
              // thing. Awaited, because `transitionModals` reads the status to choose its
              // delay — 2.5s while something full-screen is still up, 400ms once it is not.
              await AppStatus.set('idle')

              if (openModal) {
                AppStatus.transitionModals([openModal, 'hereWeGo'])
              } else {
                AppStatus.setModal('hereWeGo')
              }
            } else if (openModal) {
              AppStatus.authFinished()
              AppStatus.set('done')
            } else {
              AppStatus.set('done')
              navigate(landing.take() ?? '/')
            }

            // Signing in is over, so the flow goes back to having no state. Leaving `ready`
            // behind made the next attempt start from the end of the previous one.
            authStore.reset()
          })
          .catch(error => {
            // The profile exists — it was just created or just resolved — and only the read
            // that follows failed. Without this the person was left on the form with no
            // navigation, no alert and no state change, and resubmitting failed on an
            // address now in use.
            logger.error('Could not refresh the profile after signing in', error, 'Auth')

            AppStatus.set('idle')
            authStore.reset()

            alert(I18N.t('defaultAlert.body'))
          })

        return
      }

      case 'failed':
        AppStatus.set('idle')
        authStore.reset()

        // Backing out is not something to report back.
        if (state.reason === 'cancelled') return

        // An address that already works through another provider gets the message that says
        // which one, because knowing whether to try Google or Apple is the actionable part.
        if (state.reason === 'conflict' && state.message) {
          alert(I18N.t('Login.accountWithOtherProvider.bodyWithProviders', { possibleProviders: state.message }))
          return
        }

        alert(state.message ?? I18N.t('defaultAlert.body'))
    }
  }, [state])
}
